import { Field, FieldRenderProps } from 'react-final-form';
import styled from 'styled-components/macro';
import * as validators from 'validators';
import { CalculatorFormValues } from 'common/types';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { combineValidators } from 'validators/combineValidators';
import * as formFormatters from '@net0-calc/common/form-utils/formatters';
import * as formParsers from '@net0-calc/common/form-utils/parsers';
import { VenueGroup, VenueName, venues, venuesGroupsMap } from 'common/constants/venues';
import { DoubleCalendar, Input, SelectField, StepDescription, StepTitle } from '@net0-calc/common/components/ui';
import { WizardPageProps } from '@net0-calc/common/types';
import { DEVICE, TYPOGRAPHY } from '@net0-calc/common/assets/styles';

const translationPrefix = 'events.pages.durationAndVenue';

export const DurationAndVenuePage: FC<WizardPageProps<CalculatorFormValues>> = ({ form, values }) => {
  const { t } = useTranslation();

  const venuesGroups = values?.eventVenuesGroup ? venuesGroupsMap[values.eventVenuesGroup] : [];

  const venuesGroupsOptions = Object.values(VenueGroup).map(venueGroup => ({
    value: venueGroup,
    label: venueGroup,
  }));

  const venuesOptions = [
    ...venuesGroups.map(venueName => ({
      value: venueName,
      label: venueName,
    })),
  ];

  const changeEventVenueSizeByVenue = (value: string | string[]) => {
    if (!form) {
      return;
    }
    const newVenue = venues.find(({ name }) => name === value);

    console.log('newVenue?.m2Size', newVenue?.m2Size);
    form.change('eventVenueSize', newVenue?.m2Size);
  };

  const changeEventVenueByGroup = (value: string | string[]) => {
    if (!form || Array.isArray(value)) {
      return;
    }

    if (value === VenueGroup.OTHER) {
      changeEventVenueSizeByVenue(VenueName.OTHER);
      return form.change('eventVenue', undefined);
    }

    const defaultVenueByGroup = venuesGroupsMap[value as VenueGroup]?.[0];

    if (!defaultVenueByGroup) return;

    form.change('eventVenue', defaultVenueByGroup);
    changeEventVenueSizeByVenue(defaultVenueByGroup);
  };

  const isVenueWithSolarPanels =
    values?.eventVenuesGroup === VenueGroup.GRIMALDI_FORUM ||
    values?.eventVenuesGroup === VenueGroup.HOTEL_MONTE_CARLO_BAY;

  const isOtherVenue = values?.eventVenuesGroup === VenueGroup.OTHER;

  return (
    <Root>
      <StepTitle>{t(translationPrefix + '.title')}</StepTitle>
      <StepDescription>{t(translationPrefix + '.subTitle')}</StepDescription>

      <FormWrap>
        <Field name="eventDurationDates" validate={validators.isRequired}>
          {({ input, meta }: FieldRenderProps<any>) => (
            <StyledDoubleCalendar {...input} meta={meta} defaultValue={input.value} />
          )}
        </Field>
        <Field
          name="eventDurationHours"
          type="number"
          pattern="[0-9]*"
          parse={formParsers.toNumber}
          format={formFormatters.numberToString}
          validate={combineValidators(validators.isNumber, validators.isNegativeNumber, validators.isRequired)}>
          {field => <StyledInput label={t(translationPrefix + '.form.field.eventDurationHours.label')} {...field} />}
        </Field>
        <InputTip>{t(translationPrefix + '.form.field.eventDurationHours.tip')}</InputTip>
        <Field<string> name="eventVenuesGroup" validate={validators.isRequired}>
          {field => (
            <StyledSelectField
              options={venuesGroupsOptions}
              label={t(translationPrefix + '.form.field.eventVenusGroup.label')}
              onAfterChange={changeEventVenueByGroup}
              isSearchable
              {...field}
            />
          )}
        </Field>
        <Field<string> name="eventVenue" validate={validators.isRequired}>
          {field =>
            isOtherVenue ? (
              <StyledInput
                label={t(translationPrefix + '.form.field.eventVenue.label')}
                placeholder={t('events.pages.eventSummary.form.field.eventName.placeholder')}
                {...field}
              />
            ) : (
              <SelectField
                options={venuesOptions}
                label={t(translationPrefix + '.form.field.eventVenue.label')}
                onAfterChange={changeEventVenueSizeByVenue}
                isSearchable
                {...field}
              />
            )
          }
        </Field>
        <InputTip>{isVenueWithSolarPanels ? t(translationPrefix + '.form.field.eventVenue.tip') : ''}</InputTip>
        <Field
          name="eventVenueSize"
          type="text"
          parse={formParsers.toNumber}
          format={formFormatters.numberToString}
          validate={combineValidators(validators.isNumber, validators.isNegativeNumber)}>
          {field => (
            <StyledInput label={t(translationPrefix + '.form.field.eventSpaceSize.label')} placeholder="0" {...field} />
          )}
        </Field>
      </FormWrap>
    </Root>
  );
};

const Root = styled.div`
  width: 100%;
  margin-bottom: 60px;

  @media ${DEVICE.laptop} {
    margin-bottom: 70px;
  }
`;

const FormWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledDoubleCalendar = styled(DoubleCalendar)`
  margin-bottom: 20px;
  z-index: 1;
  @media ${DEVICE.laptop} {
    margin-bottom: 24px;
  }
`;

const StyledInput = styled(Input)`
  white-space: nowrap;
  & > div {
    margin-bottom: 5px;
  }
`;

const InputTip = styled.p`
  ${TYPOGRAPHY.captionRegular14};
  display: block;
  margin-top: 8px;
  margin-bottom: 24px;
  color: var(--color9);

  @media ${DEVICE.laptop} {
    margin-top: 12px;
  }
`;

const StyledSelectField = styled(SelectField)`
  margin-bottom: 24px;
`;
